import { useEffect, useState } from 'react'

import { getLsItem, REMOVABLE_VALUE, setLsItem } from '~/utils/localStorage'

const CUSTOMER_LISTENER_KEY = 'cc-storage'
const target = new EventTarget()

function useLocalStorage<T>(key: string): [T | undefined, (value?: T) => void] {
  // State to store our value
  const [value, setValue] = useState<T | undefined>(getLsItem(key))

  useEffect(() => {
    const handleStorageChange: EventListenerOrEventListenerObject = e => {
      const event = e as CustomEvent<T | undefined>
      setLsItem(key, event.detail)
      setValue(event.detail === REMOVABLE_VALUE ? undefined : event.detail)
    }
    target.addEventListener(`${CUSTOMER_LISTENER_KEY}:${key}`, handleStorageChange)
    return () => target.removeEventListener(`${CUSTOMER_LISTENER_KEY}:${key}`, handleStorageChange)
  }, [key])

  const handleSetValue = (newValue?: T | undefined) => {
    try {
      const valueToStore = newValue instanceof Function ? newValue(value) : newValue
      target.dispatchEvent(
        new CustomEvent<T>(`${CUSTOMER_LISTENER_KEY}:${key}`, {
          // Emit REMOVABLE_VALUE instead of undefined because when `undefined` is dispatched the listener (handleStorageChange) receives `null`.
          detail: valueToStore === undefined ? REMOVABLE_VALUE : valueToStore,
        }),
      )
    } catch (error) {
      console.error(error)
    }
  }

  // Return correct type based on whether initialValue is provided
  return [value, handleSetValue]
}

export default useLocalStorage
